import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Authentication token storage
 * 
 * SECURITY NOTE: Currently using localStorage for JWT tokens.
 * This is vulnerable to XSS attacks. For production environments with 
 * sensitive data, consider migrating to httpOnly cookies.
 * 
 * TODO: Migrate to httpOnly cookie-based authentication
 * - Backend: Set tokens via Set-Cookie header with httpOnly, Secure, SameSite flags
 * - Frontend: Remove token from localStorage, rely on automatic cookie sending
 */

// Add auth token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Tasks API
export const tasksApi = {
  list: (params) => axios.get(`${API}/tasks`, { params }),
  pending: () => axios.get(`${API}/tasks/pending`),
  get: (id) => axios.get(`${API}/tasks/${id}`),
  create: (data) => axios.post(`${API}/tasks`, data),
  assign: (id, data) => axios.put(`${API}/tasks/${id}/assign`, data),
  accept: (id) => axios.put(`${API}/tasks/${id}/accept`),
  start: (id, userLat = null, userLon = null) => {
    const params = {};
    if (userLat !== null && userLon !== null) {
      params.user_lat = userLat;
      params.user_lon = userLon;
    }
    return axios.put(`${API}/tasks/${id}/start`, null, { params });
  },
  complete: (id, { confirmEarly = false } = {}) => axios.put(`${API}/tasks/${id}/complete${confirmEarly ? '?confirm_early=true' : ''}`),
  reportProblem: (id, data) => axios.put(`${API}/tasks/${id}/problem`, data),
  delete: (id) => axios.delete(`${API}/tasks/${id}`),
  uploadPhoto: (id, file, verificationData = null, gpsCoords = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (verificationData) {
      formData.append('verification_data', JSON.stringify(verificationData));
    }
    if (gpsCoords && gpsCoords.latitude && gpsCoords.longitude) {
      formData.append('photo_lat', gpsCoords.latitude.toString());
      formData.append('photo_lon', gpsCoords.longitude.toString());
    }
    return axios.post(`${API}/tasks/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProblemPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/tasks/${id}/problem-photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Admin actions
  reject: (id, data) => axios.put(`${API}/tasks/${id}/reject`, data),
  reset: (id) => axios.put(`${API}/tasks/${id}/reset`),
  reschedule: (id, data) => axios.put(`${API}/tasks/${id}/reschedule`, data),
  addControlComment: (id, data) => axios.put(`${API}/tasks/${id}/control-comment`, data),
  uploadControlPhoto: (id, formData) => axios.post(`${API}/tasks/${id}/control-photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateOptions: (id, data) => axios.put(`${API}/tasks/${id}/options`, data),
  // Checklist
  updateChecklist: (id, data) => axios.put(`${API}/tasks/${id}/checklist`, data),
  getChecklistItems: () => axios.get(`${API}/checklist-items`)
};

// Properties API
export const propertiesApi = {
  list: (params) => axios.get(`${API}/properties`, { params }),
  get: (id) => axios.get(`${API}/properties/${id}`),
  create: (data) => axios.post(`${API}/properties`, data),
  update: (id, data) => axios.put(`${API}/properties/${id}`, data),
  delete: (id) => axios.delete(`${API}/properties/${id}`)
};

// Users API
export const usersApi = {
  list: (params) => axios.get(`${API}/users`, { params }),
  listAssignable: () => axios.get(`${API}/users`, { params: { assignable: true } }),
  get: (id) => axios.get(`${API}/users/${id}`),
  create: (data) => axios.post(`${API}/auth/register`, data),
  update: (id, data) => axios.put(`${API}/users/${id}`, data),
  delete: (id) => axios.delete(`${API}/users/${id}`)
};

// Payments API
export const paymentsApi = {
  list: (params) => axios.get(`${API}/payments`, { params }),
  markPaid: (id, method) => axios.put(`${API}/payments/${id}/paid?payment_method=${method}`),
  getMonthlySummary: (month, year) => axios.get(`${API}/payments/monthly-summary`, { params: { month, year } }),
  markAllPaid: (housekeeperId, month, year, method = 'virement') => 
    axios.put(`${API}/payments/mark-all-paid/${housekeeperId}?payment_method=${method}&month=${month}&year=${year}`)
};

// Dashboard API
export const dashboardApi = {
  admin: () => axios.get(`${API}/dashboard/admin`),
  housekeeper: () => axios.get(`${API}/dashboard/housekeeper`)
};

// Reports API
export const reportsApi = {
  export: (params) => axios.get(`${API}/reports/export`, { 
    params,
    responseType: 'blob'
  })
};

// Unavailabilities API
export const unavailabilitiesApi = {
  list: (params) => axios.get(`${API}/unavailabilities`, { params }),
  getByRange: (dateFrom, dateTo) => axios.get(`${API}/unavailabilities/by-date-range`, { 
    params: { date_from: dateFrom, date_to: dateTo } 
  }),
  create: (data) => axios.post(`${API}/unavailabilities`, data),
  delete: (id) => axios.delete(`${API}/unavailabilities/${id}`)
};

// Photos URL helper
export const getPhotoUrl = (path, token) => {
  if (!path) return '';
  // Use query parameter to handle special characters
  return `${API}/photo?path=${encodeURIComponent(path)}&auth=${token}`;
};

// Init admin
export const initAdmin = () => axios.post(`${API}/init-admin`);

// Admin cleanup - Supprimer toutes les données
export const adminApi = {
  cleanupAll: () => axios.post(`${API}/admin/cleanup-all`)
};

// Invoices / Factures API
export const invoicesApi = {
  submit: (formData, month, amount, notes) => {
    const params = new URLSearchParams();
    params.append('month', month);
    params.append('amount', amount);
    if (notes) params.append('notes', notes);
    
    return axios.post(`${API}/invoices/submit?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getMyInvoices: () => axios.get(`${API}/invoices/my`),
  getAllInvoices: () => axios.get(`${API}/invoices/all`),
  updateStatus: (invoiceId, status) => axios.put(`${API}/invoices/${invoiceId}/status?status=${status}`)
};

// Damage items API (admin only)
export const damageItemsApi = {
  list: () => axios.get(`${API}/damage-items`),
  create: (data) => axios.post(`${API}/damage-items`, data),
  update: (id, data) => axios.patch(`${API}/damage-items/${id}`, data),
  delete: (id) => axios.delete(`${API}/damage-items/${id}`),
};

// Deposits API (admin + public)
export const depositsApi = {
  list: (params) => axios.get(`${API}/deposits`, { params }),
  create: (data) => axios.post(`${API}/deposits`, data),
  get: (id) => axios.get(`${API}/deposits/${id}`),
  update: (id, data) => axios.patch(`${API}/deposits/${id}`, data),
  delete: (id) => axios.delete(`${API}/deposits/${id}`),
  capture: (id, payload) => axios.post(`${API}/deposits/${id}/capture`, payload),
  release: (id) => axios.post(`${API}/deposits/${id}/release`),
  // Public (no token needed)
  getPublic: (token) => axios.get(`${API}/deposits/public/${token}`),
  start: (token, data) => axios.post(`${API}/deposits/public/${token}/start`, data),
  createPaymentIntent: (token) => axios.post(`${API}/deposits/public/${token}/create-payment-intent`),
  confirm: (token, payload) => axios.post(`${API}/deposits/public/${token}/confirm`, payload),
};
